'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import type {
  GeneralLayout,
  Menu,
} from '@eliadmin/core/src/api/general/types/general-layout-types';
import { HeaderCartButton } from '@eliadmin/core/src/components/header/header-cart-button';
import { HeaderFavoritesButton } from '@eliadmin/core/src/components/header/header-favorites-button';
import { HeaderProfileButton } from '@eliadmin/core/src/components/header/header-profile-button';
import { MobileSearchProvider } from '@eliadmin/core/src/components/header/mobile-search';
import { SearchButton } from '@eliadmin/core/src/components/header/search-button';
import { useLocalePathname } from '@eliadmin/core/src/hooks/use-locale-pathname';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { DesktopSidebar } from './desktop-sidebar';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { arePathsEqual, getSafeHref } from '@eliadmin/core/src/utils/url-utils';
import MobileSidebar from '@eliadmin/core/src/components/header/mobile-sidebar';
import { HeaderLanguageMultistore } from './header-language-multistore';
import { useCurrentStore } from '../../utils/store-utils';

export type HeaderMainProps = {
  generalLayout: GeneralLayout['data'];
  menu: Menu[];
};

export function HeaderMain({ generalLayout, menu }: HeaderMainProps) {
  const pathname = useLocalePathname();

  const isHomepage =
    arePathsEqual(pathname, ROUTES.HOME()) || arePathsEqual(pathname, ROUTES.THANK_YOU());

  const { currentStore } = useCurrentStore();

  if (isHomepage) return null;

  return (
    <MobileSearchProvider>
      <div
        className={cn(
          'flex w-full flex-col items-center justify-center bg-transparent',
          'ea-header-main__container',
        )}
      >
        <div className={cn('custom-section', 'py-2.5 sm:py-3 md:py-4 lg:py-5 xl:py-6')}>
          <div className={cn('custom-container', 'flex items-center justify-between')}>
            <div className="flex items-center justify-start gap-4">
              <div className="flex items-center justify-center lg:hidden">
                <MobileSidebar menu={menu[0] && menu[0]?.fields} />
              </div>
              <div className="hidden items-center justify-center lg:flex">
                <DesktopSidebar isHomepage={false} />
              </div>
              <div className="hidden items-center justify-center lg:flex">
                <SearchButton variant="alternative-default" />
              </div>
            </div>
            <Link prefetchOnHover href={ROUTES.HOME()}>
              {currentStore === 'najlacnejsie-altanky.sk' ? (
                <Image
                  src={'/images/sk-logo/black-logo.svg'}
                  alt={resolveImageAlt(
                    generalLayout.header?.logo,
                    generalLayout.contact?.company || 'Logo',
                  )}
                  width={480}
                  height={160}
                  quality={100}
                  className="h-[26px] w-auto object-contain object-center sm:h-8 lg:h-12"
                  sizes="(min-width: 1024px) 360px, 240px"
                />
              ) : (
                <Image
                  src={generalLayout.header?.logo.url || '/images/placeholder.png'}
                  alt={resolveImageAlt(generalLayout?.header?.logo, generalLayout?.contact?.company || 'Logo')}
                  width={480}
                  height={160}
                  quality={100}
                  className="h-[26px] w-auto object-contain object-center sm:h-8 lg:h-12"
                  sizes="(min-width: 1024px) 360px, 240px"
                />
              )}
            </Link>
            <div className="hidden items-center justify-end gap-2 lg:flex xl:gap-4">
              <HeaderProfileButton />
              <HeaderFavoritesButton />
              <HeaderCartButton />
              <div className={cn('h-[6px] w-[6px] rounded-full bg-black/5')} />
              <HeaderLanguageMultistore isHomepage={false} />
            </div>
            <div className="flex items-center justify-center lg:hidden">
              <SearchButton variant="alternative-default" />
            </div>
          </div>
        </div>
        <div className={cn('hidden h-[1px] w-full bg-black/5 md:flex')} />
        <div
          className={cn('custom-section', '!hidden py-4 md:flex', 'ea-header-main__menu-container')}
        >
          <div
            className={cn(
              'custom-container',
              'flex items-center justify-center gap-4 lg:gap-5 xl:gap-6',
            )}
          >
            {menu[0]?.fields.map(field => (
              <Link
                key={field.name}
                prefetchOnHover
                href={getSafeHref(field?.path ?? '#')}
                className={cn('text-sm tracking-tight text-black lg:text-base')}
              >
                {field.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </MobileSearchProvider>
  );
}
